import { CommonModule, DatePipe, Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ViewModalEnum } from 'src/app/enums/operation';
import { CarouselComponent } from 'src/app/layouts/carousel/carousel.component';
import { AddJobOffer, AddJobOfferCertification, AddJobOfferSkill, AddJobOfferBenefit } from 'src/app/models/job-offer/add-job-offer';
import { GetJobOffer } from 'src/app/models/job-offer/get-job-offer';
import { DropDownKeywords, DropdownOption } from 'src/app/models/shared/dropdown-option';
import { AlertService } from 'src/app/services/alert.service';
import { DropdownService } from 'src/app/services/dropdown.service';
import { JobOfferService } from 'src/app/services/job-offer.service';
import { FormErrorsComponent } from '../../form-errors/form-errors.component';
import { DropdownComponent } from '../../shared/dropdown/dropdown.component';

@Component({
  selector: 'app-create-job-offer-companies',
  templateUrl: './create-job-offer-companies.component.html',
  styles: [],
  standalone: true,
  imports: [CommonModule, CarouselComponent, FormsModule, ReactiveFormsModule, NgxMaskDirective, FormErrorsComponent, DropdownComponent]
})
export class CreateJobOfferCompaniesComponent implements OnInit {

  jobOfferId: number = 0;
  viewEnums = ViewModalEnum;
  viewType: ViewModalEnum = ViewModalEnum.Create;

  private jobService = inject(JobOfferService);
  private formBuilder = inject(FormBuilder);
  private alertService = inject(AlertService);
  private dropdownService = inject(DropdownService);

  jobForm!: FormGroup;

  isLoading: boolean = false;
  isSaving: boolean = false;
  jobOfferFound: GetJobOffer | null | undefined = null;

  contractTypeList: DropdownOption[] = [];
  jobStatusList: DropdownOption[] = [];
  educationLevelList: DropdownOption[] = [];
  workExperienceLevelList: DropdownOption[] = [];
  departmentList: DropdownOption[] = [];
  positionList: DropdownOption[] = [];
  skillList: DropdownOption[] = [];
  certificationList: DropdownOption[] = [];
  benefitList: DropdownOption[] = [];
  requirementList: DropdownOption[] = [];
  languageList: DropdownOption[] = [];
  languageLevelList: DropdownOption[] = [];
  jobApplicationMethodList: DropdownOption[] = [];
  jobDurationLevelList: DropdownOption[] = [];
  remoteWorkPossibilities: DropdownOption[] = [];
  travelDisponibilities: DropdownOption[] = [];
  countryList: DropdownOption[] = [];
  stateList: DropdownOption[] = [];
  cityList: DropdownOption[] = [];

  constructor(private activatedRoute: ActivatedRoute,
    private datePipe: DatePipe,
    private router: Router,
    private location: Location
  ) { }

  ngOnInit(): void {

    this.buildForm();
    
    // Setup listener for position changes to suggest title
    

    this.activatedRoute.params.subscribe({
      next: (param) => {
        const offerId = param['jobOfferId'];
        this.jobOfferId = Number(offerId || '0');

        const viewType = param['viewType'];
        this.viewType = Number(viewType || ViewModalEnum.Create);

        if(this.viewType != ViewModalEnum.Create && this.jobOfferId > 0){
          this.getJobOffer();
        }
      }
    })

    this.loadDropdowns();
    this.loadCountries();
  }

  buildForm(){
    this.jobForm = this.formBuilder.group({
      id: [null],
      title: ['', Validators.required],
      departmentId: [null],
      positionId: [null],
      contractTypeId: [null, Validators.required],
      proposedSalary: [null],
      statusId: [null, Validators.required],
      description: [null, Validators.required],
      responsabilities: [null],
      requirenments: [null],
      benefits: [null],
      languagesRequired: [null],
      travelDisponibilityId: [null],
      needEmploymentAuthorization: [null],
      educationLevelId: [null],
      workExperienceLevelId: [null],
      contactInfoName: [null],
      contactInfoEmail: [null, Validators.email],
      contactInfoPhone: [null],
      jobApplicationMethodId: [null],
      publicOn: [null],
      closeOn: [null],
      contractDurationId: [null],
      address: [null],
      countryId: [null],
      stateId: [null],
      cityId: [null],
      remoteWorkPossibilityId: [null],
      isEnabled: [null],
      skills: this.formBuilder.array([]),
      certifications: this.formBuilder.array([]),
      benefitsList: this.formBuilder.array([]),
      requirementsList: this.formBuilder.array([]),
      requiredLanguages: this.formBuilder.array([]),
      selectedSkillId: [null],
      selectedCertificationId: [null],
      selectedBenefitId: [null],
      selectedRequirementId: [null],
      selectedLanguageId: [null],
      selectedLanguageLevelId: [null],
      isPrivate: [true]
    });

    this.jobForm.get('cityId')?.valueChanges.subscribe(cityId => {
      if(!cityId) {
        this.jobForm.patchValue({ city: null }, { emitEvent: false });
        return;
      }
      const foundCity = this.cityList.find(ct => ct.id === +cityId);
      this.jobForm.patchValue({ city: foundCity?.text || null }, { emitEvent: false });
    });

    this.jobForm.get('positionId')?.valueChanges.subscribe(positionId => {
      if(positionId && this.positionList?.length > 0) {
        const position = this.positionList.find(p => p.id === +positionId);
        if(position &&  !this.jobForm.get('title')?.value) {
          // Suggest using the position name as the title
          Swal.fire({
            title: 'Sugerencia',
            text: '\u00bfDesea utilizar "' + position.text + '" como t\u00edtulo para la oferta laboral?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0d1174',
            cancelButtonColor: '#3a3a3a',
            confirmButtonText: 'S\u00ed, usar',
            cancelButtonText: 'No'
          }).then((result: {isConfirmed: boolean}) => {
            if (result.isConfirmed) {
              this.jobForm.get('title')?.setValue(position.text);
            }
          });
        }
      }
    });
  }

  getJobOffer(){
    this.isLoading = true;

    this.jobService.getById(this.jobOfferId).subscribe({
      next: (res) => {
        this.jobOfferFound = res;
        if(res){
          this.patchJobOfferForm(res);
          
        }
      },
      error: (err) => {
        if (err) {
          this.alertService.showErrorAlert(err[0]);
        } else {
          this.alertService.showErrorAlert('Error al consultar la información de la oferta laboral');
        }
      }
    }).add(() => {
      this.isLoading = false;
    });
  }

  patchJobOfferForm(data: GetJobOffer): void {
    this.jobForm.patchValue({
      ...data,
      publicOn: data.publicOn ? this.datePipe.transform(data.publicOn, 'yyyy-MM-dd') : null,
      closeOn: data.closeOn ? this.datePipe.transform(data.closeOn, 'yyyy-MM-dd') : null
    });

    // Reset skills and certifications
    this.skills.clear();
    this.certifications.clear();
    this.benefitsList.clear();
    this.requirementsList.clear();
    this.requiredLanguages.clear();

    data.skills?.forEach(skill => this.addSkill(skill));
    data.certifications?.forEach(cert => this.addCertification(cert));
    data.benefitsList?.forEach(benefit => this.addBenefit(benefit));
    data.requirementsList?.forEach(requirement => this.addRequirement(requirement));
    data.requiredLanguages?.forEach(language => this.addLanguage(language));

    if(data.countryId){
      this.loadStates(data?.countryId, () => {
        if(data?.stateId) this.loadCities(data.stateId);
      });
    }

    if(this.viewType == ViewModalEnum.View){
      this.jobForm.disable();
    }
  }

  createSkill(skill?: AddJobOfferSkill): FormGroup {
    return this.formBuilder.group({
      id: [skill?.id],
      skillId: [skill?.skillId],
      name: [skill?.name],
      isEnabled: [skill?.isEnabled],
    });
  }

  createCertification(cert?: AddJobOfferCertification): FormGroup {
    return this.formBuilder.group({
      id: [cert?.id],
      certificationId: [cert?.certificationId],
      name: [cert?.name],
      isEnabled: [cert?.isEnabled],
    });
  }

  createBenefit(benefit?: AddJobOfferBenefit): FormGroup {
    return this.formBuilder.group({
      id: [benefit?.id],
      benefitId: [benefit?.benefitId],
      name: [benefit?.name],
      isEnabled: [benefit?.isEnabled],
    });
  }

  createRequirement(requirement?: any): FormGroup {
    return this.formBuilder.group({
      id: [requirement?.id],
      requirementId: [requirement?.requirementId],
      name: [requirement?.name],
      isEnabled: [requirement?.isEnabled],
    });
  }

  createLanguage(language?: any): FormGroup {
    return this.formBuilder.group({
      id: [language?.id],
      languageId: [language?.languageId],
      name: [language?.name],
      levelId: [language?.levelId],
      levelName: [language?.levelName],
      isEnabled: [language?.isEnabled],
    });
  }

  get skills(): FormArray {
    return this.jobForm.get('skills') as FormArray;
  }

  get certifications(): FormArray {
    return this.jobForm.get('certifications') as FormArray;
  }

  get benefitsList(): FormArray {
    return this.jobForm.get('benefitsList') as FormArray;
  }

  get requirementsList(): FormArray {
    return this.jobForm.get('requirementsList') as FormArray;
  }

  get requiredLanguages(): FormArray {
    return this.jobForm.get('requiredLanguages') as FormArray;
  }

  addSkill(skill?: AddJobOfferSkill): void {
    this.skills.push(this.createSkill(skill));
  }

  addCertification(cert?: AddJobOfferCertification): void {
    this.certifications.push(this.createCertification(cert));
  }

  addBenefit(benefit?: AddJobOfferBenefit): void {
    this.benefitsList.push(this.createBenefit(benefit));
  }

  addRequirement(requirement?: any): void {
    this.requirementsList.push(this.createRequirement(requirement));
  }

  addLanguage(language?: any): void {
    this.requiredLanguages.push(this.createLanguage(language));
  }

  addSkillFromSelect() {
    const selected = this.jobForm.get('selectedSkillId')?.value
    if (!selected) return
    const found = this.skillList.find(s => s.id === selected)
    if (!found) return
    this.addSkill({
      skillId: found.id,
      name: found.text,
      isEnabled: true
    })
    this.jobForm.patchValue({ selectedSkillId: null })
  }

  addCertificationFromSelect() {
    const selected = this.jobForm.get('selectedCertificationId')?.value
    if (!selected) return
    const found = this.certificationList.find(s => s.id === selected)
    if (!found) return
    this.addCertification({
      certificationId: found.id,
      name: found.text,
      isEnabled: true
    })
    this.jobForm.patchValue({ selectedCertificationId: null })
  }

  addBenefitFromSelect() {
    const selected = this.jobForm.get('selectedBenefitId')?.value
    if (!selected) return
    const found = this.benefitList.find(b => b.id === selected)
    if (!found) return
    this.addBenefit({
      benefitId: found.id,
      name: found.text,
      isEnabled: true
    })
    this.jobForm.patchValue({ selectedBenefitId: null })
  }

  addRequirementFromSelect() {
    const selected = this.jobForm.get('selectedRequirementId')?.value
    if (!selected) return
    const found = this.requirementList.find(r => r.id === selected)
    if (!found) return
    this.addRequirement({
      requirementId: found.id,
      name: found.text,
      isEnabled: true
    })
    this.jobForm.patchValue({ selectedRequirementId: null })
  }

  addLanguageFromSelect() {
    const languageId = this.jobForm.get('selectedLanguageId')?.value;
    const languageLevelId = this.jobForm.get('selectedLanguageLevelId')?.value;
    
    if (!languageId || !languageLevelId) return;
    
    const foundLanguage = this.languageList.find(l => l.id === languageId);
    const foundLevel = this.languageLevelList.find(l => l.id === languageLevelId);
    
    if (!foundLanguage || !foundLevel) return;
    
    this.addLanguage({
      languageId: foundLanguage.id,
      name: foundLanguage.text,
      levelId: foundLevel.id,
      levelName: foundLevel.text,
      isEnabled: true
    });
    
    this.jobForm.patchValue({ 
      selectedLanguageId: null,
      selectedLanguageLevelId: null
    });
  }

  removeSkill(index: number) {
    const skill = this.skills.controls[index];
    const id = skill.get('id')?.value;
    if (id > 0) {
      this.skills.controls[index].get('isEnabled')?.setValue(false);
    } else {
      this.skills.removeAt(index);
    }
  }

  removeCertification(index: number) {
    const certification = this.certifications.controls[index];
    const id = certification.get('id')?.value;
    if (id > 0) {
      this.certifications.controls[index].get('isEnabled')?.setValue(false);
    } else {
      this.certifications.removeAt(index);
    }
  }

  removeBenefit(index: number) {
    const benefit = this.benefitsList.controls[index];
    const id = benefit.get('id')?.value;
    if (id > 0) {
      this.benefitsList.controls[index].get('isEnabled')?.setValue(false);
    } else {
      this.benefitsList.removeAt(index);
    }
  }

  removeRequirement(index: number) {
    const requirement = this.requirementsList.controls[index];
    const id = requirement.get('id')?.value;
    if (id > 0) {
      this.requirementsList.controls[index].get('isEnabled')?.setValue(false);
    } else {
      this.requirementsList.removeAt(index);
    }
  }

  removeLanguage(index: number) {
    const language = this.requiredLanguages.controls[index];
    const id = language.get('id')?.value;
    if (id > 0) {
      this.requiredLanguages.controls[index].get('isEnabled')?.setValue(false);
    } else {
      this.requiredLanguages.removeAt(index);
    }
  }

  loadDropdowns() {
    forkJoin([
      this.dropdownService.GetOptions(DropDownKeywords.ContractTypes),
      this.dropdownService.GetOptions(DropDownKeywords.JobOfferStatuses),
      this.dropdownService.GetOptions(DropDownKeywords.EducationLevels),
      this.dropdownService.GetOptions(DropDownKeywords.WorkExperienceLevels),
      this.dropdownService.GetOptions(DropDownKeywords.Skills),
      this.dropdownService.GetOptions(DropDownKeywords.Certifications),
      this.dropdownService.GetOptions(DropDownKeywords.JobApplicationMethods),
      this.dropdownService.GetOptions(DropDownKeywords.JobContractDurations),
      this.dropdownService.GetOptions(DropDownKeywords.RemoteWorkPossibilities),
      this.dropdownService.GetOptions(DropDownKeywords.TravelDisponibilities),
      this.dropdownService.GetOptions(DropDownKeywords.JobBenefits),
      this.dropdownService.GetOptions(DropDownKeywords.GeneralJobRequirements),
      this.dropdownService.GetOptions(DropDownKeywords.Languages, 1),
      this.dropdownService.GetOptions(DropDownKeywords.LanguageLevels),
      this.dropdownService.GetOptions(DropDownKeywords.Departments),
      this.dropdownService.GetOptions(DropDownKeywords.Positions),
    ]).subscribe({
      next:(res)=>{
        this.contractTypeList = res[0];
        this.jobStatusList = res[1];
        this.educationLevelList = res[2];
        this.workExperienceLevelList = res[3];
        this.skillList = res[4];
        this.certificationList = res[5];
        this.jobApplicationMethodList = res[6];
        this.jobDurationLevelList = res[7];
        this.remoteWorkPossibilities = res[8];
        this.travelDisponibilities = res[9];
        this.benefitList = res[10];
        this.requirementList = res[11];
        this.languageList = res[12];
        this.languageLevelList = res[13];
        this.departmentList = res[14];
        this.positionList = res[15];
      } 
    })
  }

  loadCountries() {
    this.dropdownService.GetOptions(DropDownKeywords.Countries).subscribe({
      next: (res) => {
        this.countryList = res;
      }
    });
  }

  onChangeCountry() {
    const countryId = this.jobForm.value.countryId;
    this.jobForm.patchValue({ stateId: null, cityId: null, countryName: null }); // Reiniciamos y limpiamos countryName
    this.stateList = [];
    this.cityList = [];
    if(countryId) {
      this.loadStates(countryId, () => {
        const found = this.countryList.find(c => c.id === +countryId);
        this.jobForm.patchValue({ countryName: found?.text || null });
      });
    }
  }

  loadStates(countryId: number, callback?: () => void) {
    const typeId = 1; // Provinces by countryId
    this.dropdownService.GetOptions(DropDownKeywords.Provinces,  typeId, `${countryId}`).subscribe({
      next: (res) => {
        this.stateList = res;
        callback?.();
      }
    });
  }

  onChangeState() {
    const stateId = this.jobForm.value.stateId;
    this.jobForm.patchValue({ cityId: null, stateName: null }); // Limpieza de stateName
    this.cityList = [];
    if(stateId) {
      this.loadCities(stateId, () => {
        const found = this.stateList.find(s => s.id === +stateId);
        this.jobForm.patchValue({ stateName: found?.text || null });
      });
    }
  }

  loadCities(stateId: number, callback?: () => void) {
    const typeId = 1; // Provinces by stateId
    this.dropdownService.GetOptions(DropDownKeywords.Cities, typeId, `${stateId}`).subscribe({
      next: (res) => {
        this.cityList = res;
        callback?.();
      }
    });
  }

  submit(){
    if (this.jobForm.invalid) {
      const missingFields = this.getMissingRequiredFields(this.jobForm);

      this.alertService.showWarningAlert('Verifique los campos del formulario');
      this.jobForm.markAllAsTouched();
      return;
    }

    // Prevenir múltiples envíos
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    const model: any = this.jobForm.getRawValue();

    this.jobService.UpsertJobOffer(model).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Oferta laboral guardada exitosamente');
        this.router.navigate(['/companies/job-offers'])
      },
      error: (err) => {
        if(err){
          this.alertService.showErrorAlert(err[0]);
        }else{
          this.alertService.showErrorAlert('Error al guardar');
        }
        
      }
    }).add( () => {
      this.isLoading = false;
      this.isSaving = false;
    });
  }

  getMissingRequiredFields(form: FormGroup): string[] {
    const missingFields: string[] = [];

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control && control.invalid && control.errors?.['required']) {
        missingFields.push(key); // Store the name of the missing required field
      }
    });

    return missingFields;
  }

  onCancel(): void {
    this.location.back();
  }
}
